export interface IOrderReportBucket {
  status?: string
  count: number
  quantity: number
  codTotal: number
  transferTotal: number
  grandTotal: number // ยอดสั่งทั้งหมด — รวมออเดอร์ที่ยกเลิก (ไม่ใช่ยอดขายจริง)
  // แยกผลลัพธ์ออเดอร์ออกจาก grandTotal (นับทุกใบ ไม่ดูว่าจ่ายแล้วหรือยัง)
  successCount: number // จำนวนออเดอร์สำเร็จ (COMPLETED + DELIVERED)
  successTotal: number // ยอดขายสถานะสำเร็จ — ตัวที่ใช้คิดยอดขาย/กำไร
  successCostTotal: number // ต้นทุนของออเดอร์สำเร็จ; กำไรขั้นต้น = successTotal - successCostTotal
  cancelledCount: number // จำนวนออเดอร์ยกเลิก
  cancelledTotal: number // ยอดของออเดอร์ที่ยกเลิก
  cancelledCostTotal: number // ต้นทุนที่ติดมากับออเดอร์ยกเลิก (ของไม่ได้ขายจริง)
  // ต้นทุนสินค้า (COGS) — snapshot จาก product.cost ณ วันขาย, นับทุกออเดอร์
  costTotal: number // ต้นทุนรวมทุกสถานะ (รวมใบยกเลิก) — คิดกำไรตรง ๆ ไม่ได้
  costMissingCount: number // จำนวน line item ที่หาต้นทุนไม่เจอ (SKU ยังไม่ผูก) — >0 = ต้นทุนไม่ครบ
  // settlement (net payout จาก Finance API) — นับเฉพาะ order ที่ statement ออกแล้ว
  revenueTotal: number // ยอดขายฐาน settlement ก่อนหักค่าธรรมเนียม (revenue + fee = net)
  netTotal: number // ยอดที่ได้รับจริงหลังหักค่าธรรมเนียม
  feeTotal: number // ค่าธรรมเนียมรวม (ค่าลบ)
  commissionTotal: number // ค่าคอมมิชชั่น TikTok Shop
  settledCount: number // จำนวน order ที่จ่ายแล้ว

  // รายละเอียดการชำระเงิน (แตกจาก statement_transaction)
  // ยอดขาย: grossSales + sellerDiscount + salesRefund = revenue
  grossSalesTotal: number // ราคาป้ายก่อนส่วนลด
  sellerDiscountTotal: number // ส่วนลดที่ร้านออกเอง (ค่าลบ)
  salesRefundTotal: number // ยอดขายที่ยกเลิก/คืน (ค่าลบ)
  salesRefundGrossTotal: number // ↳ ราคาเต็มส่วนที่คืน
  salesRefundSellerTotal: number // ↳ ส่วนลดร้านส่วนที่คืน
  platformDiscountTotal: number // คูปอง TikTok ออกให้ลูกค้า (ค่าลบ, ไม่หักร้าน)
  customerPaymentTotal: number // เงินที่ลูกค้าจ่ายจริง
  // ค่าธรรมเนียม: commission + affiliate + shippingFee + shippingSubsidy + otherFee = feeTotal
  affiliateTotal: number // ค่า affiliate รวม (ค่าลบ)
  affiliateCommissionTotal: number // ↳ ค่าคอม affiliate
  affiliateAdsTotal: number // ↳ ค่าโฆษณา affiliate
  affiliatePartnerTotal: number // ↳ ค่าพาร์ทเนอร์ affiliate
  shippingFeeTotal: number // ค่าส่งที่หักจากร้าน (ค่าลบ)
  shippingSubsidyTotal: number // TikTok ช่วยจ่ายค่าส่ง (ค่าบวก)
  otherFeeTotal: number // ค่าธรรมเนียมที่ TikTok ไม่แตกรายการ (ส่งฟรี + ธุรกรรม)
  refundTotal: number // เงินคืนลูกค้า (ค่าลบ)
  adjustmentTotal: number // TikTok ชดเชย/เรียกคืน
}

export interface IOrderReport {
  summary: IOrderReportBucket
  byStatus: IOrderReportBucket[]
}

export interface IReportFilter {
  shopId?: string
  startDate?: string // YYYY-MM-DD
  endDate?: string // YYYY-MM-DD
  by?: 'order' | 'settlement' // มุมมองรายงาน: ตามวันสั่ง (default) หรือตามรอบจ่าย
}

// ---- Daily report (API ใหม่ GET /tiktok/report/orders-daily) ----
// bucket เดียวกับ order report (success/cancelled + settlement + breakdown ครบ) + date
// → ส่งเข้า SettlementBreakdownTable ต่อวันได้ (expand row)
export interface IDailyReportRow extends Omit<IOrderReportBucket, 'status'> {
  date: string // YYYY-MM-DD (Asia/Bangkok)
}

// วันที่มีแต่ค่าใช้จ่าย (ไม่มียอดขาย) → เติม 0 ทุก field ให้ครบ type
export const EMPTY_DAILY_ROW: IDailyReportRow = {
  date: '',
  count: 0,
  quantity: 0,
  codTotal: 0,
  transferTotal: 0,
  grandTotal: 0,
  successCount: 0,
  successTotal: 0,
  successCostTotal: 0,
  cancelledCount: 0,
  cancelledTotal: 0,
  cancelledCostTotal: 0,
  costTotal: 0,
  costMissingCount: 0,
  revenueTotal: 0,
  netTotal: 0,
  feeTotal: 0,
  commissionTotal: 0,
  settledCount: 0,
  grossSalesTotal: 0,
  sellerDiscountTotal: 0,
  salesRefundTotal: 0,
  salesRefundGrossTotal: 0,
  salesRefundSellerTotal: 0,
  platformDiscountTotal: 0,
  customerPaymentTotal: 0,
  affiliateTotal: 0,
  affiliateCommissionTotal: 0,
  affiliateAdsTotal: 0,
  affiliatePartnerTotal: 0,
  shippingFeeTotal: 0,
  shippingSubsidyTotal: 0,
  otherFeeTotal: 0,
  refundTotal: 0,
  adjustmentTotal: 0,
}

export interface IDailyReport {
  records: IDailyReportRow[]
}

// ---- Cost summary (GET /costs/summary — aggregate ฝั่ง DB, ไม่ paginate) ----
export interface ICostSummaryResponse {
  total: number
  byDate: { date: string; amount: number }[]
}

// ---- แถวตารางรายวัน (รวมยอดขาย + ค่าใช้จ่าย) ----
export interface IDailyDisplayRow extends IDailyReportRow {
  cost: number
  estimatedProfit: number // calcEstimatedProfit
  settledProfit: number // calcSettledProfit
}

// ---- List sale_orders ต่อสถานะ (GET /tiktok/report/orders/list — cursor keyset) ----
export interface ISaleOrderListItem {
  order_id: string
  status: string
  is_cod: boolean
  total_amount: number
  quantity: number
  currency: string
  order_date: string // ISO
  settled: boolean
  net_amount: number // settlement.net_amount (0 ถ้ายังไม่ settle)
  cost_total: number // ต้นทุนสินค้ารวมของ order
  cost_missing_count: number // >0 = ต้นทุน order นี้ไม่ครบ (บาง SKU ยังไม่ผูก)
}

export interface IListSaleOrdersResult {
  items: ISaleOrderListItem[]
  nextCursor: string | null // null = หมดแล้ว
}

// ---- SKU ที่ยังไม่ผูก (GET /tiktok/report/orders/unlinked-skus) — 1 แถว = 1 SKU ต่อร้าน ----
export interface IUnlinkedSku {
  shop_id: string
  sku_id: string
  seller_sku: string
  product_id: string
  product_name: string // ชื่อจากคำสั่งซื้อล่าสุด
  order_count: number // จำนวนคำสั่งซื้อที่ต้นทุนไม่ครบเพราะ SKU นี้
  quantity: number
  last_order_date: string // ISO
}

export interface IListUnlinkedSkusResult {
  items: IUnlinkedSku[] // เรียงจำนวนคำสั่งซื้อมาก → น้อย
}

// ---- Order detail (GET /tiktok/report/order/:orderId) ----
// settlement.breakdown = ยอดแยกรายการต่อ order (snake_case จาก platform ISettlementBreakdown)
export interface ISaleOrderSettlementBreakdown {
  gross_sales: number
  seller_discount: number
  sales_refund: number
  sales_refund_gross: number
  sales_refund_seller: number
  platform_discount: number
  customer_payment: number
  affiliate: number
  affiliate_commission: number
  affiliate_ads: number
  affiliate_partner: number
  shipping_fee: number
  shipping_subsidy: number
  other_fee: number
  refund: number
  adjustment: number
}

export interface ISaleOrderSettlement {
  statement_id: string
  statement_time: number
  revenue: number
  fee_total: number
  commission: number
  net_amount: number
  currency: string
  breakdown?: ISaleOrderSettlementBreakdown
}

// ต้นทุนต่อรายการสินค้า (snapshot ณ วันขาย) — merge เข้าตารางสินค้าด้วย seller_sku
export interface ISaleOrderCostLineItem {
  seller_sku: string
  product_id: string
  sku_id: string
  product_name: string
  quantity: number
  unit_cost: number
  cost: number
  resolved: boolean
}

export interface ISaleOrderDetail {
  order_id: string
  shop_id: string
  status: string
  is_cod: boolean
  total_amount: number
  sub_total: number
  currency: string
  quantity: number
  order_date: string // ISO
  create_time: number
  paid_time: number
  raw: Record<string, any> // TikTok 202309 order snapshot (line_items / recipient_address / payment)
  line_items?: ISaleOrderCostLineItem[] // ต้นทุนต่อรายการ (อาจไม่มีใน order เก่าที่ยังไม่ backfill)
  cost_total: number // ต้นทุนสินค้ารวมของ order
  cost_missing_count: number // >0 = ต้นทุนไม่ครบ (บาง SKU ยังไม่ผูก)
  settled: boolean
  settlement: ISaleOrderSettlement | null
}

// ---- สถานะ backfill ต้นทุน (GET /tiktok/backfill-cost/status) ----
export interface ICostBackfillStatus {
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | null // null = ยังไม่เคยรัน
  scanned: number // จำนวนที่ตั้งใจทำในรอบล่าสุด
  updated: number // เขียนต้นทุนกลับสำเร็จ
  stillMissing: number // ยังหาต้นทุนไม่เจอหลังรัน
  error: string | null
  startedAt: string | null
  completedAt: string | null
  pending: number // คำสั่งซื้อที่ต้นทุนยังไม่ครบ ณ ตอนนี้ (นับสด)
}

// ---- ตัดสต๊อกย้อนหลัง (GET/POST /tiktok/backfill-stock) ----
// รายการที่ TikTok สั่งตัดมาตอนร้านยังไม่ผูก SKU → สต๊อกยังไม่ถูกตัด
export interface IStockBackfillTally {
  count: number // จำนวนรายการ
  quantity: number // จำนวนชิ้น
}

export interface IStockBackfillProduct {
  objectId: string
  code: string
  name: string
  isCollection?: boolean
}

export interface IStockBackfillSku extends IStockBackfillTally {
  shopId?: string
  productId?: string
  skuId?: string
  sku?: string
  // ready = หาสินค้าในระบบเจอ 1 ตัว ตัดได้ / not_found = ยังไม่ผูก / ambiguous = ผูกซ้ำหลายตัว
  status: 'ready' | 'not_found' | 'ambiguous'
  product?: IStockBackfillProduct
  candidates?: IStockBackfillProduct[] // เฉพาะ ambiguous — สินค้าที่ผูก SKU นี้ซ้ำกันอยู่
}

export interface IStockBackfillPreview {
  skus: IStockBackfillSku[] // เฉพาะของออเดอร์ที่ยังไม่ยกเลิก
  cancelled: IStockBackfillTally // ออเดอร์ยกเลิกแล้ว → ปิดทิ้ง ไม่ตัด
  unknown: IStockBackfillTally // ไม่รู้สถานะออเดอร์ → ข้าม
}

export interface IStockBackfillStatus {
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | null // null = ยังไม่เคยรัน
  shopId: string | null
  totalOrders: number
  processedOrders: number
  deducted: IStockBackfillTally
  cancelled: IStockBackfillTally
  skipped: IStockBackfillTally
  failed: IStockBackfillTally
  error: string | null
  startedAt: string | null
  completedAt: string | null
  pendingOrders: number // ออเดอร์ที่ยังมีรายการค้าง ณ ตอนนี้ (นับสด)
}
